import React from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getLocationReturnPath, useNavigateBack } from '../../hooks/useNavigateBack';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui';
import { DocumentTextIcon } from '../../components/ui/Icons';
import { createCrewFile, updateCrewFile } from '../../services/mutations';
import CrewFileTabs from './CrewFileTabs';
import { CrewIdentityHeader } from './CrewIdentityHeader';
import type { CrewMediaCommitHandle } from './CrewPhotoSignaturePanel';
import {
  formToDashboardPreview,
  crewFileToForm,
  emptyCrewForm,
  formToPayload,
  type CrewFormState,
} from './formMappers';
import { moduleConfig } from './config';
import { parseCrewRouteId } from './crewRouteId';
import { useCrewFile, useCrewLookups } from './useData';
import { useAuth } from '../../contexts/AuthContext';

const Form: React.FC = () => {
  const { colors } = useTheme();
  const { user } = useAuth();
  const agencyMode = String(user?.typeLocation ?? '').toUpperCase() === 'MANNING_AGENT';
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = getLocationReturnPath(location.state) || moduleConfig.module.route.list;
  const goBack = useNavigateBack(moduleConfig.module.route.list, returnPath);
  const { id } = useParams<{ id: string }>();
  const crewId = parseCrewRouteId(id);
  const isEdit = Boolean(id);
  const editIdValid = !isEdit || crewId != null;
  const mediaRef = React.useRef<CrewMediaCommitHandle>(null);

  const { data: existing, isLoading } = useCrewFile(crewId);
  const { lookups, isLoading: loadingLookups } = useCrewLookups();
  const [form, setForm] = React.useState<CrewFormState>(emptyCrewForm);
  const [saving, setSaving] = React.useState(false);
  const [hydrated, setHydrated] = React.useState(!isEdit);
  const [activeSection, setActiveSection] = React.useState(() =>
    agencyMode ? 'personal' : 'dashboard',
  );

  React.useEffect(() => {
    if (!isEdit || !existing) return;
    setForm(crewFileToForm(existing));
    setHydrated(true);
  }, [isEdit, existing]);

  if (isEdit && !editIdValid) {
    return <Navigate to="/crew/list" replace />;
  }

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter') return;
    const el = e.target as HTMLElement;
    if (el.tagName === 'TEXTAREA') return;
    if (el.closest('button[type="submit"]')) return;
    e.preventDefault();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.lastName.trim() || !form.firstName.trim()) {
      setActiveSection('personal');
      showError('Last name and first name are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (isEdit && crewId) {
        await updateCrewFile(crewId, payload);
        await mediaRef.current?.commit();
        showSuccess('Crew file saved.');
        navigate(
          agencyMode ? moduleConfig.module.route.file(crewId) : moduleConfig.module.route.detail(crewId),
          {
            state: { from: moduleConfig.module.route.edit(crewId) },
          },
        );
      } else {
        const created = await createCrewFile(payload);
        showSuccess('Crew file created.');
        navigate(
          agencyMode
            ? moduleConfig.module.route.file(created.id)
            : moduleConfig.module.route.detail(created.id),
          {
            state: { from: moduleConfig.module.route.list },
          },
        );
      }
    } catch (err: any) {
      showError(err?.response?.data?.message ?? 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if ((isEdit && isLoading) || !hydrated || loadingLookups) {
    return (
      <div className={cn('flex items-center justify-center flex-1 min-h-[240px] text-sm', colors.textMuted)}>
        Loading...
      </div>
    );
  }

  const submitText = isEdit ? 'Save' : 'Create';
  const preview = formToDashboardPreview(form, lookups, crewId ?? 0);
  const hasName = Boolean(form.firstName.trim() || form.lastName.trim());

  return (
    <div className="h-full flex flex-col min-h-0">
      <form
        id="crew-file-form"
        noValidate
        onSubmit={handleSubmit}
        onKeyDown={handleFormKeyDown}
        className={cn(
          'flex-1 flex flex-col min-h-0 overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <CrewIdentityHeader
          item={preview}
          embedded
          onBack={goBack}
          title={!isEdit && !hasName ? 'New crew file' : undefined}
          actions={
            isEdit && crewId ? (
              <Link
                to={moduleConfig.module.route.file(crewId)}
                state={{ from: moduleConfig.module.route.edit(crewId) }}
              >
                <Button type="button" variant="outline" size="sm">
                  <DocumentTextIcon className="h-4 w-4" />
                  View
                </Button>
              </Link>
            ) : undefined
          }
        />

        <CrewFileTabs
          form={form}
          setForm={setForm}
          lookups={lookups}
          crewId={crewId ?? 0}
          documents={existing?.documents}
          mediaRef={mediaRef}
          activeSection={activeSection}
          onActiveSectionChange={setActiveSection}
          sectionAccess={lookups?.sectionAccess ?? (existing as any)?.sectionAccess}
          agencyMode={agencyMode}
        />

        <div
          className={cn(
            'flex-shrink-0 flex items-center justify-end gap-3 px-5 py-4 border-t',
            colors.border,
            colors.surfaceHover,
          )}
        >
          <Button type="button" variant="outline" onClick={goBack}>
            Cancel
          </Button>
          <Button type="submit" variant="accent" disabled={saving} loading={saving}>
            {submitText}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Form;
